import React, { useEffect, useMemo, useRef, useState } from "react";

import { CButton, CSpinner } from "@coreui/react";

import FormularioRichContent from "./FormularioRichContent";
import {
  TEMPO_PADRAO,
  formatarRelogio,
  isTempoIlimitado,
  letraDaOpcao
} from "./opcoes";
import URL from "../../config/api";
import axios from "../../config/axios";
import "./formulario.css";

const CONFETTI = Array.from({ length: 72 }, (_, index) => ({
  id: index,
  left: `${(index * 47) % 100}%`,
  delay: `${(index % 18) * 0.09}s`,
  duration: `${4.6 + (index % 9) * 0.22}s`,
  top: `${-24 - (index % 9) * 86}px`,
  drift: `${((index * 29) % 181) - 90}px`,
  sway: `${((index * 43) % 121) - 60}px`,
  swayBack: `${((index * 61) % 101) - 50}px`,
  color: ["#facc15", "#e34d70", "#2e9cca", "#46a968", "#8b5cf6"][index % 5]
}));

/* Execução de um formulário pelo aluno. Mesma mecânica do player do quiz —
   tempo por pergunta, uma pergunta por vez, XP ao concluir —, com duas
   diferenças que vêm da regra do formulário:

   - Não há resposta certa, então não há feedback de acerto/erro: respondeu,
     segue para a próxima.
   - Cada aluno responde uma vez só. Se ele saiu no meio, o backend devolve a
     mesma resposta com `retomada` e a lista do que já foi respondido, e o
     player recomeça da primeira pergunta pendente. Se já enviou, o backend
     devolve 409 e o player mostra que o formulário já foi respondido.

   O tempo de cada pergunta é medido do momento em que ela aparece até a
   resposta, e vai em `tempo_resposta` com ou sem limite. Com limite, o
   relógio conta para trás e a pergunta vai sem resposta quando zera; sem
   limite (tempo_limite 0), o relógio só mostra quanto já passou. */

const limiteDaPergunta = pergunta =>
  isTempoIlimitado(pergunta?.tempo_limite)
    ? null
    : Number(pergunta?.tempo_limite) || TEMPO_PADRAO;

export default function FormularioActivityPlayer({
  token,
  formularioId,
  activityId,
  allocationId,
  onFinished,
  onClose
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jaRespondido, setJaRespondido] = useState(false);
  const [resposta, setResposta] = useState(null);
  const [perguntas, setPerguntas] = useState([]);
  const [totalPerguntas, setTotalPerguntas] = useState(0);
  const [jaRespondidas, setJaRespondidas] = useState(0);
  const [indice, setIndice] = useState(0);
  // Segundos desde que a pergunta atual apareceu
  const [decorrido, setDecorrido] = useState(0);
  const inicioDaPergunta = useRef(Date.now());
  const [respondendo, setRespondendo] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const playerRef = useRef(null);

  const pergunta = perguntas[indice];
  const limite = limiteDaPergunta(pergunta);
  const restante = limite === null ? null : Math.max(0, limite - decorrido);
  const percentualTempo = useMemo(
    () =>
      limite === null
        ? 100
        : Math.max(0, Math.min(100, (restante / limite) * 100)),
    [limite, restante]
  );

  const iniciarPergunta = () => {
    inicioDaPergunta.current = Date.now();
    setDecorrido(0);
  };

  /* Pelo relógio do sistema, e não pelo contador na tela: o setTimeout atrasa
     com a aba em segundo plano, e o tempo gravado seria menor que o real. */
  const tempoGasto = () => {
    const segundos = Math.round((Date.now() - inicioDaPergunta.current) / 1000);
    return limite === null ? segundos : Math.min(segundos, limite);
  };

  const finalizar = async responseId => {
    const retorno = await axios.post(
      `${URL.api}formulario/responses/${responseId}/finish`,
      {},
      { headers: { authorization: `Bearer ${token}` } }
    );
    setResultado(retorno.data?.data || null);
  };

  useEffect(() => {
    let ativo = true;
    axios
      .post(
        `${URL.api}formulario/${formularioId}/responses`,
        {
          atividade_id: activityId,
          allocation_id: allocationId
        },
        { headers: { authorization: `Bearer ${token}` } }
      )
      .then(async retorno => {
        if (!ativo) return;
        if (!retorno.data?.success)
          throw new Error(
            retorno.data?.error || "Não foi possível iniciar o formulário."
          );
        const dados = retorno.data.data;
        const todas = dados.perguntas || [];
        const respondidas = dados.retomada
          ? (dados.perguntas_respondidas || []).map(Number)
          : [];
        const pendentes = todas.filter(
          item => !respondidas.includes(Number(item.id))
        );

        setResposta(dados);
        setTotalPerguntas(todas.length);
        setJaRespondidas(todas.length - pendentes.length);
        setPerguntas(pendentes);
        iniciarPergunta();

        /* Saiu depois de responder a última pergunta e antes do envio: não
           sobra nada a responder, só falta finalizar. */
        if (!pendentes.length) await finalizar(dados.id);
      })
      .catch(err => {
        if (!ativo) return;
        if (err.response?.status === 409) setJaRespondido(true);
        else setError(err.response?.data?.error || err.message);
      })
      .finally(() => {
        if (ativo) setLoading(false);
      });
    return () => {
      ativo = false;
    };
  }, [formularioId, activityId, allocationId, token]);

  const responder = async opcaoId => {
    if (!pergunta || respondendo || resultado) return;
    setRespondendo(true);
    try {
      await axios.post(
        `${URL.api}formulario/responses/${resposta.id}/answers`,
        {
          question_id: pergunta.id,
          option_id: opcaoId,
          tempo_resposta: tempoGasto()
        },
        { headers: { authorization: `Bearer ${token}` } }
      );

      if (indice + 1 >= perguntas.length) {
        await finalizar(resposta.id);
        return;
      }
      setIndice(valor => valor + 1);
      iniciarPergunta();
      setRespondendo(false);
    } catch (err) {
      setError(
        err.response?.data?.error || "Não foi possível registrar a resposta."
      );
      setRespondendo(false);
    }
  };

  // Relógio: acompanha o tempo real, e não "um segundo a cada tique"
  useEffect(() => {
    if (!pergunta || respondendo || resultado) return undefined;
    const timer = window.setInterval(
      () =>
        setDecorrido(
          Math.floor((Date.now() - inicioDaPergunta.current) / 1000)
        ),
      250
    );
    return () => window.clearInterval(timer);
  }, [pergunta, respondendo, resultado]);

  // Com limite, a pergunta vai sem resposta quando o tempo acaba
  useEffect(() => {
    if (!pergunta || respondendo || resultado || limite === null) return;
    if (decorrido >= limite) responder(null);
  }, [decorrido, limite, pergunta, respondendo, resultado]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(document.fullscreenElement === playerRef.current);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement === playerRef.current) {
        await document.exitFullscreen();
      } else {
        await playerRef.current?.requestFullscreen();
      }
    } catch (_error) {
      // O navegador pode negar tela cheia quando a página perde o foco.
    }
  };

  const fullscreenButton = (
    <button
      type="button"
      className="formulario-ava-player__fullscreen"
      onClick={toggleFullscreen}
      aria-label={fullscreen ? "Sair da tela cheia" : "Abrir em tela cheia"}
      aria-pressed={fullscreen}
      title={fullscreen ? "Sair da tela cheia" : "Tela cheia"}
    >
      <i
        className={`fa fa-${fullscreen ? "compress" : "expand"}`}
        aria-hidden="true"
      />
    </button>
  );

  if (loading)
    return (
      <div className="text-center p-5">
        <CSpinner />
      </div>
    );
  /* Já respondido também conclui: o aluno pode ter enviado pelo menu
     Formulário e só depois abrir o mesmo formulário dentro da atividade, e o
     item não pode ficar pendente para sempre por isso. */
  if (jaRespondido)
    return (
      <div className="p-5 text-center">
        <h4>Você já respondeu este formulário.</h4>
        <p className="text-muted">
          Cada formulário é respondido uma vez só. Obrigado pela participação!
        </p>
        <CButton
          color="secondary"
          onClick={() => {
            onFinished?.();
            onClose?.();
          }}
        >
          Fechar
        </CButton>
      </div>
    );
  if (error)
    return (
      <div className="p-5 text-center">
        <p className="text-danger">{error}</p>
        <CButton color="secondary" onClick={onClose}>
          Fechar
        </CButton>
      </div>
    );
  if (resultado) {
    return (
      <div
        ref={playerRef}
        className="formulario-ava-player formulario-ava-player--finished"
      >
        <div className="formulario-result-overlay">
          <div className="formulario-confetti" aria-hidden="true">
            {CONFETTI.map(particle => (
              <i
                key={particle.id}
                style={{
                  left: particle.left,
                  animationDelay: particle.delay,
                  animationDuration: particle.duration,
                  backgroundColor: particle.color,
                  top: particle.top,
                  "--formulario-confetti-drift": particle.drift,
                  "--formulario-confetti-sway": particle.sway,
                  "--formulario-confetti-sway-back": particle.swayBack
                }}
              />
            ))}
          </div>
          <section className="formulario-result-modal">
            <div
              className="formulario-result-modal__celebration"
              aria-hidden="true"
            >
              ✓
            </div>
            <h2>Respostas enviadas!</h2>
            <p className="formulario-result-modal__message">
              Obrigado por responder o formulário.
            </p>
            <div className="formulario-result-modal__score">
              <strong>
                {resultado.respondidas ?? 0}/{resultado.total ?? 0}
              </strong>
              <span>perguntas respondidas</span>
            </div>
            {resultado.xp_concedido && (
              <div className="formulario-result-modal__xp">
                <span>+20 XP</span>
              </div>
            )}
            <CButton
              color="primary"
              className="formulario-result-modal__continue"
              onClick={() => {
                onFinished?.();
                onClose?.();
              }}
            >
              Sair do formulário
            </CButton>
          </section>
        </div>
        {fullscreenButton}
      </div>
    );
  }

  /* Perguntas já respondidas numa sessão anterior contam na numeração, para o
     aluno que retoma ver "Pergunta 4/6", e não "Pergunta 1/3". */
  return (
    <div ref={playerRef} className="formulario-ava-player">
      <header className="formulario-ava-player__header">
        <span>
          Pergunta {jaRespondidas + indice + 1}/{totalPerguntas}
        </span>
        {limite === null ? (
          <strong
            className="is-elapsed"
            title="Tempo nesta pergunta"
            aria-label={`Tempo nesta pergunta: ${formatarRelogio(decorrido)}`}
          >
            {formatarRelogio(decorrido)}
          </strong>
        ) : (
          <strong className={restante <= 5 ? "is-danger" : ""}>
            {restante}
          </strong>
        )}
      </header>
      {/* Sem limite não há o que esvaziar: a barra só existe com prazo */}
      <div className="formulario-ava-player__progress">
        {limite !== null && <span style={{ width: `${percentualTempo}%` }} />}
      </div>
      <main className="formulario-ava-player__body">
        {pergunta && (
          <div className="formulario-ava-player__content" key={pergunta.id}>
            <div className="formulario-ava-player__question">
              <FormularioRichContent value={pergunta.enunciado} readOnly />
            </div>
            <div className="formulario-ava-player__options">
              {pergunta.opcoes.map((opcao, opcaoIndice) => (
                <button
                  key={opcao.id}
                  type="button"
                  disabled={respondendo}
                  onClick={() => responder(opcao.id)}
                >
                  <b>{letraDaOpcao(opcaoIndice)}</b>
                  <span>
                    <FormularioRichContent value={opcao.texto} readOnly />
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </main>
      {fullscreenButton}
    </div>
  );
}
